import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable } from '@angular/core';
import {
	ComponentController,
	filterNil,
	localeDateToDateTime,
} from '@lycoris-nubila/ngx-lycoris';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { uniq } from 'lodash-es';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, combineLatest } from 'rxjs';
import {
	debounceTime,
	filter,
	map,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs/operators';

import { AddUserNoteDialogComponent } from 'dialog/add-user-note-dialog/add-user-note-dialog.component';
import { SortDirection } from 'infrastructure/common/model/api.sort-direction';
import { CoacheeProfileNotesFilterForm } from 'presentation/page/profile/coachee-profile/coachee-profile-notes/coachee-profile-notes.model';
import {
	CoacheeProfileNotesState,
	CoacheeProfileNotesStore,
} from 'presentation/page/profile/coachee-profile/coachee-profile-notes/coachee-profile-notes.store';
import { CoacheeProfileController } from 'presentation/page/profile/coachee-profile/coachee-profile.controller';
import { IUserNote } from 'user/note/user-note';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';

@Injectable()
@UntilDestroy()
export class CoacheeProfileNotesController extends ComponentController<CoacheeProfileNotesState> {
	public coachee$: Observable<IUser>;
	public sessionUser$: Observable<IUser>;
	public coacheeNotes$: Observable<IUserNote[]>;
	public dateSortCssIconClass$: Observable<string>;
	public isHandsetLandscape$: Observable<boolean>;
	public filterForm: FormGroup<CoacheeProfileNotesFilterForm>;

	public i18n =
		'presentation.page.profile.coachee-profile.coachee-profile-notes';

	constructor(
		store: CoacheeProfileNotesStore,
		private _coacheeProfileController: CoacheeProfileController,
		private _userApplicationPort: UserApplicationPort,
		private _dialogService: DialogService,
		private _breakpointObserver: BreakpointObserver,
		private _translateService: TranslateService
	) {
		super(store);

		this.filterForm = new FormGroup<CoacheeProfileNotesFilterForm>({
			dateSort: new FormControl<SortDirection>('DESC'),
			note: new FormControl<string>(''),
			date: new FormControl<Date>(),
		});

		this._initObservables();
		this._initListeners();
	}

	public loadNextPage() {
		this.store.update((state) => ({
			notesOffset: (state.notesOffset ?? 0) + state.notesChunkSize,
		}));
	}

	public deleteNote(noteId: string): Promise<void> {
		return this._userApplicationPort.deleteUserNote(noteId);
	}

	private _initObservables() {
		this.isHandsetLandscape$ = this._breakpointObserver
			.observe(Breakpoints.HandsetLandscape)
			.pipe(
				map((result) => result.matches),
				shareReplay({ refCount: true, bufferSize: 1 })
			);

		this.coachee$ = this._coacheeProfileController.coachee$;

		this.dateSortCssIconClass$ = this.filterForm.value$.pipe(
			map((value) =>
				value.dateSort === 'DESC'
					? 'fak fa-str-arrow-down-wide-short'
					: 'fak fa-str-arrow-up-short-wide'
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.coacheeNotes$ = this.select('notesId').pipe(
			switchMap((notesId) => this._userApplicationPort.observeNotes(notesId)),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.sessionUser$ = this._userApplicationPort
			.observeSessionUser()
			.pipe(shareReplay({ refCount: true, bufferSize: 1 }));
	}

	public toggleDateSort() {
		this.filterForm.patchValue({
			dateSort: this.filterForm.value.dateSort === 'DESC' ? 'ASC' : 'DESC',
		});
	}

	private _initListeners() {
		this._coacheeProfileController.bottomReached$
			.pipe(
				filterNil(),
				filter((bottomReached) => bottomReached.activeTabIndex === 0),
				tap(() =>
					this.store.update((state) => ({
						notesOffset: state.notesId.length,
					}))
				)
			)
			.subscribe();

		this.filterForm.value$
			.pipe(
				debounceTime(250),
				tap(() =>
					this.store.update({
						notesOffset: 0,
						reloadNotesTrigger: Date.now(),
					})
				),
				untilDestroyed(this)
			)
			.subscribe();

		combineLatest([
			this._coacheeProfileController.coachee$.pipe(filterNil()),
			this._userApplicationPort.observeSessionUserId(),
			this.select(['notesOffset', 'notesChunkSize', 'reloadNotesTrigger']),
		])
			.pipe(
				switchMap(([coachee, userId, data]) =>
					this._userApplicationPort
						.SearchUserCoachNotes({
							startOfDayTimestamp: this.filterForm.value.date
								? localeDateToDateTime(this.filterForm.value.date, coachee?.timeZone)
										.startOf('day')
										.toMillis()
								: null,
							endOfDayTimestamp: this.filterForm.value.date
								? localeDateToDateTime(this.filterForm.value.date, coachee?.timeZone)
										.endOf('day')
										.toMillis()
								: null,
							sortDirection: this.filterForm.value.dateSort || 'DESC',
							note: this.filterForm.value.note,
							chunkSize: data.notesChunkSize,
							offset: data.notesOffset,
							userId: userId,
							targetId: coachee?.id,
						})
						.pipe(
							filter((result) => !(result instanceof Error)),
							tap((notesId: string[]) =>
								data.notesOffset === 0
									? this.store.update({ notesId })
									: this.store.update((state) => ({
											notesId: uniq([...state.notesId, ...notesId]),
									  }))
							)
						)
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	public addUserNote(coacheeId: string) {
		this._dialogService
			.open(AddUserNoteDialogComponent, {
				header: this._translateService.instant(
					'presentation.app.common.dialog.addUserNote.title'
				),
				data: { coacheeId: coacheeId },
				width: '500px',
			})
			.onClose.subscribe(() =>
				this.store.update({ notesOffset: 0, reloadNotesTrigger: Date.now() })
			);
	}
}
